/**
 * src/shell/pillar_home.js
 *
 * A1 "springboard" — a pillar's landing content (DESIGN_SPEC §3): a hero
 * greeting band + optional Favorites/Recent chip rows + a Quick Actions
 * column + an Apps tile grid (that pillar's role-visible screens). Registered
 * by shell.js as the route target for `/pillar/{id}`; clicking a quick action
 * or an app tile navigates to that screen's own route via the hash router.
 *
 * Presentation-only redesign: same data (greeting + Quick Actions + Apps from
 * pillars.js) — no IA change. Enhancements over the bare v1:
 *   - Hero band (--fs-hero greeting + pillar eyebrow + date + role chip).
 *   - Live-count badges on tiles whose count is cheaply available from the
 *     existing getReportsSummary read-CF (no new CF): approvals/leave/
 *     attendance = red, payroll-runs/purchase-orders = accent. 0 → nothing,
 *     >99 → "99+". Fully non-blocking + fail-silent (guardedRead).
 *   - Recents (per pillar, recorded by shell.js onRouteChange) + Favorites
 *     (per pillar, star toggle on tiles). Pure localStorage, no backend.
 *
 * All new user-facing strings route through t() with en+ar keys
 * (springboard.*); the greeting reuses the existing dashboard.greeting* keys.
 */

import { navigate } from '../lib/router.js';
import { getBootstrap } from '../lib/session-store.js';
import { callFn, guardedRead } from '../lib/api.js';
import { auth } from '../lib/firebase.js';
import { t } from '../i18n/t.js';
import { getLang } from '../i18n/store.js';
import { ALL_SCREENS } from './pillars.js';

/**
 * screenDisplayLabel — resolves a pillars.js screen/quick-action entry's
 * display label through t() AT RENDER TIME (so tiles/chips/actions flip with
 * the language toggle, like the nav), falling back to the raw EN `label`
 * for any entry without a labelKey. Root cause of the AR-mode bug this
 * fixes: tiles/chips rendered the raw EN `label` literal directly.
 */
function screenDisplayLabel(entry) {
  return entry.labelKey ? t(entry.labelKey) : entry.label;
}

/** Render-time label for a Favorites/Recent chip: resolve the ROUTE back to
 * its screen entry (labelKey → t()) so stored chips re-translate on language
 * flip. `storedLabel` is never written by recordRecentVisit/toggleFavorite
 * any more (2026-07-15 i18n fix — only `route` is persisted) — the param
 * stays only as a legacy-data fallback for a pre-fix localStorage entry
 * whose route no longer resolves in ALL_SCREENS at all. */
function chipDisplayLabel(route, storedLabel) {
  const screen = ALL_SCREENS.find((s) => s.path === route);
  return screen ? screenDisplayLabel(screen) : storedLabel || route;
}

// ── localStorage nav prefs (Recents + Favorites) — client-only, no CF ──────
const RECENTS_KEY = 'staffly.recents';
const FAVORITES_KEY = 'staffly.favorites';
const RECENTS_CAP = 5;

function readStore(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
function writeStore(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch { /* best-effort */ }
}

/** Record a real-screen visit under its owning pillar (called by shell.js's
 * onRouteChange). Most-recent-first, deduped by route, capped. Only the
 * ROUTE is stored (2026-07-15 i18n fix, see chipDisplayLabel below) — no
 * label is frozen into localStorage, so a Recents chip always re-translates
 * on a language flip instead of showing whatever language was active the
 * moment the screen was visited. */
export function recordRecentVisit(pillarId, route) {
  if (!pillarId || !route) return;
  const store = readStore(RECENTS_KEY);
  const list = Array.isArray(store[pillarId]) ? store[pillarId] : [];
  const next = [{ route }, ...list.filter((e) => e && e.route !== route)].slice(0, RECENTS_CAP);
  store[pillarId] = next;
  writeStore(RECENTS_KEY, store);
}
function getRecents(pillarId) {
  const list = readStore(RECENTS_KEY)[pillarId];
  return Array.isArray(list) ? list.filter((e) => e && e.route) : [];
}
function getFavorites(pillarId) {
  const list = readStore(FAVORITES_KEY)[pillarId];
  return Array.isArray(list) ? list.filter((e) => e && e.route) : [];
}
function isFavorite(pillarId, route) {
  return getFavorites(pillarId).some((e) => e.route === route);
}
/** Only the ROUTE is persisted (2026-07-15 i18n fix, see chipDisplayLabel
 * below) — no label is frozen into localStorage, so a Favorites chip always
 * re-translates on a language flip. */
function toggleFavorite(pillarId, route) {
  const store = readStore(FAVORITES_KEY);
  const list = Array.isArray(store[pillarId]) ? store[pillarId] : [];
  const exists = list.some((e) => e && e.route === route);
  store[pillarId] = exists
    ? list.filter((e) => e && e.route !== route)
    : [...list, { route }];
  writeStore(FAVORITES_KEY, store);
  return !exists;
}

// ── Setup-completeness nudge (2026-07-24) ──────────────────────────────────
//
// A DISMISSIBLE, non-forcing banner (rendered at the top of every pillar
// springboard) pointing admin/hr toward the new Setup hub (/setup) while
// self-service configuration is incomplete — CK's explicit ask (see
// screens/setup.js header). NEVER a modal, NEVER blocks any click, no
// wizard. Two dismissal levels: (a) × hides it for the rest of THIS session
// (in-memory module flag — resets on a fresh page load) (b) "Don't show
// again" persists per-user in localStorage (matches this file's own
// existing Recents/Favorites localStorage-prefs pattern, zero backend — a
// server-side per-user flag that follows the admin across devices is a
// fast-follow, not needed for this week's demo). Auto-retires the moment
// completedCount reaches totalCount — no state needed for that case, it
// simply never renders again. Guarded + fail-silent throughout: a broken
// getBootstrap/getSetupStatus call must NEVER break the home springboard —
// worst case, the banner just doesn't appear.
let _setupNudgeSessionDismissed = false;
const SETUP_NUDGE_DISMISS_PREFIX = 'staffly.setupNudge.';

function setupNudgeDismissKey() {
  const uid = auth.currentUser && auth.currentUser.uid;
  return uid ? `${SETUP_NUDGE_DISMISS_PREFIX}${uid}` : null;
}
function isSetupNudgePermanentlyDismissed() {
  const key = setupNudgeDismissKey();
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function dismissSetupNudgePermanently() {
  const key = setupNudgeDismissKey();
  if (!key) return;
  try { localStorage.setItem(key, '1'); } catch { /* best-effort */ }
}

/** Fetches getSetupStatus (admin/hr-gated CF) and renders the banner only
 * when this caller is admin/hr, is not dismissed, and setup is genuinely
 * incomplete. Never awaited by the caller — fires after the springboard has
 * already painted. */
function loadSetupNudge(el) {
  const host = el.querySelector('#ph-setup-nudge');
  if (!host) return;
  if (_setupNudgeSessionDismissed || isSetupNudgePermanentlyDismissed()) return;

  getBootstrap().then(({ data, timedOut, error }) => {
    if (timedOut || error || !data) return;
    const role = data.profileExists && data.profile ? data.profile.role : null;
    if (role !== 'admin' && role !== 'hr') return; // matches getSetupStatus's own role gate

    guardedRead(callFn('getSetupStatus'), { timeoutMs: 12000, label: 'springboard-setup-nudge' }).then(
      ({ data: status, timedOut: t2, error: e2 }) => {
        if (t2 || e2 || !status || status.ok !== true) return; // fail-silent, never an error state on home
        if (status.completedCount >= status.totalCount) return; // fully set up — auto-retire
        if (_setupNudgeSessionDismissed || isSetupNudgePermanentlyDismissed()) return; // re-check: async race with a dismiss click
        renderSetupNudge(host, status);
      },
    );
  });
}

function renderSetupNudge(host, status) {
  host.innerHTML = `
    <div class="ph-setup-nudge" role="status" style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:10px 16px;margin-bottom:14px;border:1px solid var(--border-a);border-radius:var(--radius-sm);background:var(--accent-glow)">
      <span style="font-size:12.5px;color:var(--text);flex:1;min-width:220px">${escapeHtml(t('springboard.setupNudgeTitle', { completed: status.completedCount, total: status.totalCount }))}</span>
      <button type="button" class="btn btn-primary btn-sm" data-setup-nudge-open>${escapeHtml(t('springboard.setupNudgeCta'))}</button>
      <button type="button" class="btn btn-ghost btn-sm" data-setup-nudge-dismiss-forever>${escapeHtml(t('springboard.setupNudgeDontShowAgain'))}</button>
      <button type="button" aria-label="${escapeAttr(t('springboard.setupNudgeDismissAria'))}" data-setup-nudge-dismiss style="background:none;border:none;color:var(--text-f);cursor:pointer;font-size:16px;line-height:1;padding:2px 4px">×</button>
    </div>`;

  host.querySelector('[data-setup-nudge-open]')?.addEventListener('click', () => navigate('/setup'));
  host.querySelector('[data-setup-nudge-dismiss]')?.addEventListener('click', () => {
    _setupNudgeSessionDismissed = true;
    host.innerHTML = '';
  });
  host.querySelector('[data-setup-nudge-dismiss-forever]')?.addEventListener('click', () => {
    dismissSetupNudgePermanently();
    _setupNudgeSessionDismissed = true;
    host.innerHTML = '';
  });
}

// ── Live counts (§3.4) — no new CF ─────────────────────────────────────────
// route → { fn: callable, field on that callable's counts, badge variant }.
//
// SAME-SCOPE INVARIANT (owner-flagged false-positive fix, 2026-07-15): a
// tile's badge must show the number the DESTINATION SCREEN itself displays.
// The Approvals badge previously read getReportsSummary.approvalsWaiting,
// which counts approval_requests on a `status == 'pending'` FIELD — but the
// canonical envelope schema (approval_mutate_service.ts EnvelopeDoc) stores
// `state`, not `status` (`status` is only a wire-rename inside
// getApprovalQueue), and approval_requests is still client-writable, so
// stray/legacy docs carrying a literal `status` field lit the badge while
// the Approvals screen (getApprovalQueue counts.pending, computed from
// `state` ∈ submitted|routing|pending) honestly showed 0. The badge now
// sources getApprovalQueue's own counts.pending — the exact number on the
// screen's KPI row. All reads stay guarded + fail-silent: error/timeout/
// denial → badge stays hidden, never a false alert.
const COUNT_SOURCES = {
  '/approvals': { fn: 'getApprovalQueue', field: 'pending', variant: 'red' },
  '/leave': { fn: 'getReportsSummary', field: 'leaveRequestsPending', variant: 'red' },
  '/attendance': { fn: 'getReportsSummary', field: 'attendanceUnapproved', variant: 'red' },
  '/payroll-runs': { fn: 'getReportsSummary', field: 'payrollRunsOpen', variant: 'accent' },
  '/purchase-orders': { fn: 'getReportsSummary', field: 'purchaseOrdersPending', variant: 'accent' },
};

// ── Icons (star for favorites, clock for recents) ──────────────────────────
const STAR_OUTLINE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const STAR_FILLED = '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const ICON_CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l2.5 2"/></svg>';

/** Current hour (0-23) in Asia/Riyadh (UTC+3, no DST) — pins the greeting's
 * morning/afternoon/evening bucket to Riyadh regardless of the viewer's device
 * timezone. Falls back to a fixed +3 UTC offset if Intl/timeZone is
 * unavailable. Never throws. */
function riyadhHour() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Riyadh', hour: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const hourPart = parts.find((p) => p.type === 'hour');
    if (hourPart) {
      const n = parseInt(hourPart.value, 10);
      if (Number.isFinite(n)) return n % 24;
    }
  } catch { /* fall through to the fixed-offset fallback */ }
  return (new Date().getUTCHours() + 3) % 24;
}

function greetingWord() {
  const hour = riyadhHour();
  if (hour < 12 && hour >= 5) return t('dashboard.greetingMorning');
  if (hour < 17 && hour >= 12) return t('dashboard.greetingAfternoon');
  return t('dashboard.greetingEvening');
}

// The 5 primary pillars have nav.* keys; resolve a translated label for them
// so the hero sub-line flips language (mirrors shell.js's pillarLabel).
const NAV_LABEL_KEYS = {
  people: 'nav.people',
  money: 'nav.money',
  operations: 'nav.operations',
  compliance: 'nav.compliance',
  insights: 'nav.insightsTools',
  verticals: 'nav.verticals',
};
function pillarLabel(pillar) {
  const key = NAV_LABEL_KEYS[pillar.id];
  return key ? t(key) : pillar.label;
}

// Role badge vocabulary (2026-07-15 i18n fix: the hero role chip used to
// render the raw role token — 'admin'/'hr'/'manager'/'finance' — verbatim in
// BOTH languages; en/ar.js's roles.* keys mirror employees.role* wording).
const ROLE_LABEL_KEYS = {
  admin: 'roles.admin',
  hr: 'roles.hr',
  manager: 'roles.manager',
  finance: 'roles.finance',
  employee: 'roles.employee',
  supervisor: 'roles.supervisor',
};
export function roleLabel(role) {
  const key = ROLE_LABEL_KEYS[role];
  return key ? t(key) : role;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}
function escapeAttr(text) {
  return String(text == null ? '' : text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function todayLabel() {
  try {
    // Plain 'ar', not 'ar-SA': un-pinned 'ar-SA' defaults to the Hijri
    // calendar on pre-CLDR-42 ICU (see scheduling.js's own comment) — 'ar'
    // is Gregorian-default and matches employee_home.js / messages.js, so the
    // three home surfaces render the same date the same way.
    const loc = getLang() === 'ar' ? 'ar' : 'en-GB';
    return new Date().toLocaleDateString(loc, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return new Date().toDateString();
  }
}

function quickActionRow(qa, goldCls) {
  return `
    <button type="button" class="qa-row${goldCls}" data-route="${escapeAttr(qa.route)}">
      <span class="qi">${qa.icon}</span>${escapeHtml(screenDisplayLabel(qa))}
    </button>`;
}

function appTile(screen, goldCls, pillarId) {
  const fav = isFavorite(pillarId, screen.path);
  const hasCount = COUNT_SOURCES[screen.path];
  const favTitle = fav ? t('springboard.removeFavorite') : t('springboard.addFavorite');
  // No data-fav-label any more (2026-07-15 i18n fix): toggleFavorite only
  // persists the route now — chips re-resolve the translated label from the
  // route at render time (chipDisplayLabel), so nothing language-specific
  // ever gets frozen into localStorage.
  return `
    <button type="button" class="app-tile${goldCls}" data-route="${escapeAttr(screen.path)}">
      <span class="app-fav${fav ? ' on' : ''}" data-fav="${escapeAttr(screen.path)}"
            role="button" tabindex="0" aria-label="${escapeAttr(favTitle)}" title="${escapeAttr(favTitle)}">${fav ? STAR_FILLED : STAR_OUTLINE}</span>
      ${hasCount ? `<span class="count-badge ${hasCount.variant}" data-count-route="${escapeAttr(screen.path)}" hidden></span>` : ''}
      <span class="ti">${screen.icon}</span>
      <span class="tl">${escapeHtml(screenDisplayLabel(screen))}</span>
    </button>`;
}

/** Builds the Favorites + Recent chip rows (each rendered only when non-empty
 * — never an empty row, per §3.5). */
function chipsHtml(pillar) {
  const favs = getFavorites(pillar.id);
  const recents = getRecents(pillar.id).filter((r) => !favs.some((f) => f.route === r.route));
  let out = '';
  if (favs.length) {
    out += `
      <div class="ph-chips">
        <span class="ph-chips-label">${escapeHtml(t('springboard.favorites'))}</span>
        ${favs.map((f) => `<button type="button" class="ph-chip fav" data-route="${escapeAttr(f.route)}">${STAR_FILLED}${escapeHtml(chipDisplayLabel(f.route, f.label))}</button>`).join('')}
      </div>`;
  }
  if (recents.length) {
    out += `
      <div class="ph-chips">
        <span class="ph-chips-label">${escapeHtml(t('springboard.recent'))}</span>
        ${recents.map((r) => `<button type="button" class="ph-chip" data-route="${escapeAttr(r.route)}">${ICON_CLOCK}${escapeHtml(chipDisplayLabel(r.route, r.label))}</button>`).join('')}
      </div>`;
  }
  return out;
}

/**
 * Quick Actions dedup-by-design (CK-flagged defect, 2026-07-15): a "quick
 * action" that routes to the exact same destination as one of this pillar's
 * own app tiles is a second link, not a distinct action, and is dropped.
 * As of the real-quick-actions wave (2026-07-15) every pillars.js
 * quickActions entry is a genuine create-verb deep link (`?action=new`
 * route ≠ any tile's bare path), so real actions always survive this
 * filter — it stays as the guard against dead-duplicate entries creeping
 * back in.
 */
function distinctQuickActions(pillar) {
  return pillar.quickActions.filter((qa) => !pillar.screens.some((s) => s.path === qa.route));
}

/**
 * mountPillarHome — `pillar` must already be role-filtered (visiblePillars
 * output from pillars.js): its `screens`/`quickActions` are exactly what
 * this caller may see, so no further gating happens here.
 */
export function mountPillarHome(el, pillar) {
  const goldCls = pillar.gold ? ' gold' : '';
  const label = pillarLabel(pillar);
  const quickActions = distinctQuickActions(pillar);
  const hasQuickActions = quickActions.length > 0;

  el.innerHTML = `
    <div class="pillar-home">
      <div id="ph-setup-nudge"></div>
      <header class="ph-hero${goldCls}">
        <div class="ph-hero-main">
          <div class="ph-eyebrow">${escapeHtml(label)}</div>
          <h1 class="ph-greeting" id="pillar-greeting">${escapeHtml(greetingWord())}</h1>
          <div class="ph-sub">${escapeHtml(t('springboard.atAGlance', { pillar: label }))}</div>
        </div>
        <div class="ph-hero-meta">
          <span class="ph-role-chip" id="ph-role-chip" hidden></span>
          <span class="ph-date">${escapeHtml(todayLabel())}</span>
        </div>
      </header>
      <div id="ph-chips">${chipsHtml(pillar)}</div>
      <div class="springboard${hasQuickActions ? '' : ' no-qa'}">
        ${hasQuickActions ? `
        <aside class="qa-panel">
          <div class="panel-title">${escapeHtml(t('springboard.quickActions'))}</div>
          <div class="qa-list">${quickActions.map((qa) => quickActionRow(qa, goldCls)).join('')}</div>
        </aside>` : ''}
        <section class="apps-panel">
          <div class="panel-title">${escapeHtml(t('springboard.apps'))}</div>
          <div class="apps-grid">${pillar.screens.map((s) => appTile(s, goldCls, pillar.id)).join('')}</div>
        </section>
      </div>
    </div>`;

  // Navigation: any element carrying data-route (tiles, quick actions, chips).
  el.querySelectorAll('[data-route]').forEach((btn) => {
    btn.addEventListener('click', () => navigate(btn.dataset.route));
  });

  // Favorite stars — toggle without navigating (stopPropagation on the tile).
  el.querySelectorAll('.app-fav').forEach((star) => {
    const toggle = (e) => {
      e.stopPropagation();
      e.preventDefault();
      const route = star.dataset.fav;
      const nowOn = toggleFavorite(pillar.id, route);
      star.classList.toggle('on', nowOn);
      star.innerHTML = nowOn ? STAR_FILLED : STAR_OUTLINE;
      const title = nowOn ? t('springboard.removeFavorite') : t('springboard.addFavorite');
      star.title = title;
      star.setAttribute('aria-label', title);
      refreshChips(el, pillar);
    };
    star.addEventListener('click', toggle);
    star.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') toggle(e);
    });
  });

  // Fill in the caller's first name + role chip once bootstrap resolves —
  // never blocks the springboard render (mirrors shell.js's populateUserChip).
  getBootstrap().then(({ data }) => {
    const profile = data && data.profileExists ? data.profile : null;
    const firstName = profile && profile.name ? String(profile.name).trim().split(/\s+/)[0] : null;
    const greetEl = el.querySelector('#pillar-greeting');
    if (greetEl && firstName) {
      greetEl.textContent = t('dashboard.greetingLine', { greeting: greetingWord(), name: firstName });
    }
    const roleChip = el.querySelector('#ph-role-chip');
    if (roleChip && profile && profile.role) {
      roleChip.textContent = roleLabel(profile.role);
      roleChip.hidden = false;
    }
  });

  // Live-count badges — one guarded call, fail-silent, non-blocking (§3.4).
  loadCounts(el);

  // Setup-completeness nudge — dismissible, non-forcing (§ above).
  loadSetupNudge(el);
}

/** Rebuild only the Favorites/Recent chip rows in place (after a star toggle),
 * re-wiring their navigation. */
function refreshChips(el, pillar) {
  const host = el.querySelector('#ph-chips');
  if (!host) return;
  host.innerHTML = chipsHtml(pillar);
  host.querySelectorAll('[data-route]').forEach((btn) => {
    btn.addEventListener('click', () => navigate(btn.dataset.route));
  });
}

/** Fetch the operational counts and paint the tile badges. One guarded call
 * per DISTINCT source callable actually needed by THIS pillar's tiles (in
 * practice: getReportsSummary for most, getApprovalQueue only on the pillar
 * carrying the Approvals tile — see the same-scope invariant above).
 * Anything missing/denied/timed-out simply leaves the badges hidden — never
 * an error state, never a false alert, on the home. */
function loadCounts(el) {
  const badgeEls = Array.from(el.querySelectorAll('[data-count-route]'));
  if (badgeEls.length === 0) return; // this pillar has no count-bearing tiles
  const fns = new Set(
    badgeEls.map((b) => (COUNT_SOURCES[b.dataset.countRoute] || {}).fn).filter(Boolean),
  );
  for (const fn of fns) {
    guardedRead(callFn(fn), { timeoutMs: 12000, label: `springboard-counts-${fn}` }).then(
      ({ data }) => {
        const counts = data && data.ok && data.counts ? data.counts : null;
        if (!counts) return; // failed/denied/timed-out read → no badge
        badgeEls.forEach((badge) => {
          const src = COUNT_SOURCES[badge.dataset.countRoute];
          if (!src || src.fn !== fn) return;
          const n = Number(counts[src.field]) || 0;
          if (n <= 0) return; // 0 → render nothing
          badge.textContent = n > 99 ? '99+' : String(n);
          badge.hidden = false;
        });
      },
    );
  }
}
